<div class="mat-typography">
    <form *ngIf="form" [formGroup]="form">
        <div class="require-banner" *ngIf="isBannedPasswordListMissing">
            <mat-icon>error_outline</mat-icon>
            <span>Custom banned password list is required when enforce custom list is enabled.</span>
        </div>
        <table>
            <tr>
                <th>Field</th>
                <th>Current Value</th>
                <th>Desired Value</th>
                <th>Status</th>
            </tr>


                <!-- Lockout threshold -->
            <tr>
                <td>
                    <div class="item">
                        Lockout threshold
                        <mat-icon
                            matTooltip="How many failed sign-ins are allowed on an account before its first lockout. If the first sign-in after a lockout also fails, the account locks out again.">info</mat-icon>
                    </div>
                </td>
                <td>{{ lockoutThresholdCurrentValue }}</td>
                <td class="desired-value">
                    <mat-form-field appearance="outline">
                        <input matInput type="number" min="1" max="50" formControlName="LockoutThreshold" />
                        <mat-error *ngIf="form.get('LockoutThreshold')?.errors?.required">
                            Lockout threshold is required.
                        </mat-error>
                        <mat-error *ngIf="form.get('LockoutThreshold')?.errors?.min">
                            Minimum allowed value is 1.
                        </mat-error>
                        <mat-error *ngIf="form.get('LockoutThreshold')?.errors?.max">
                            Maximum allowed value is 50.
                        </mat-error>
                    </mat-form-field>
                </td>
                <td>
                    <sway-baseline-status [hasError]="lockoutThresholdDeviated"></sway-baseline-status>
                </td>
            </tr>

             <!-- Lockout duration in seconds -->
            <tr>
                <td>
                    <div class="item">
                        Lockout duration in seconds
                        <mat-icon
                            matTooltip="The minimum length in seconds of each lockout. If an account locks repeatedly, this duration increases.">info</mat-icon>
                    </div>
                </td>
                <td>{{ lockoutDurationInSecondsCurrentValue }}</td>
                <td class="desired-value">
                    <mat-form-field appearance="outline">
                        <input matInput type="number" min="5" max="18000" formControlName="LockoutDurationInSeconds" />
                        <mat-error *ngIf="form.get('LockoutDurationInSeconds')?.errors?.required">
                            Lockout duration in seconds is required.
                        </mat-error>
                        <mat-error *ngIf="form.get('LockoutDurationInSeconds')?.errors?.min">
                            Minimum allowed value is 5.
                        </mat-error>
                        <mat-error *ngIf="form.get('LockoutDurationInSeconds')?.errors?.max">
                            Maximum allowed value is 18000.
                        </mat-error>
                    </mat-form-field>
                </td>
                <td>
                    <sway-baseline-status [hasError]="lockoutDurationInSecondsDeviated"></sway-baseline-status>
                </td>
            </tr>


            <!-- Enable cloud banned password check -->
            <tr>
                <td>
                    <div class="item">
                        Enforce custom list
                        <mat-icon
                            matTooltip="Enforce custom list">info</mat-icon>
                    </div>
                </td>
                <td>{{ enableBannedPasswordCheckCurrentValue }}</td>
                <td class="desired-value">
                    <mat-slide-toggle formControlName="EnableBannedPasswordCheck">
                        {{ form.get('EnableBannedPasswordCheck')?.value ? 'Enabled' : 'Disabled' }}
                    </mat-slide-toggle>
                </td>
                <td>
                    <sway-baseline-status [hasError]="enableBannedPasswordCheckDeviated"></sway-baseline-status>
                </td>
            </tr>

            <!-- Custom banned password list (only when enforcing) -->
            <tr *ngIf="isEnforceCustomListEnabled" >
                <td>
                    <div class="item">
                        Custom banned password list
                        <mat-icon
                            matTooltip="A list of words, one per line, to prevent your users from using in their passwords. You should include words specific to your organization, such as your products, trademarks, industries, local cities and towns, and local sports teams. Your list can contain up to 1000 words. These are case insensitive, and common character substitutions (o for 0, etc) are automatically considered.">info</mat-icon>
                    </div>
                </td>
                <td>{{ bannedPasswordListCurrentValue }}</td>
                <td class="desired-value">
                    <mat-form-field appearance="outline" >
                        <mat-chip-list #bannedPasswordChips [disabled]="readOnly"
                            aria-label="Custom banned password list">
                            <mat-chip *ngFor="let word of bannedPasswordWords" [value]="word"
                                color="primary" selected [removable]="!readOnly"
                                (removed)="removeBannedPassword(word)">
                                {{ word }}
                                <button matChipRemove *ngIf="!readOnly">
                                    <mat-icon>cancel</mat-icon>
                                </button>
                            </mat-chip>
                            <input *ngIf="!readOnly" placeholder="Type a word and press Enter"
                                [matChipInputFor]="bannedPasswordChips"
                                [matChipInputAddOnBlur]="true"
                                (matChipInputTokenEnd)="addBannedPassword($event)" />
                        </mat-chip-list>
                        <mat-hint>Each word must be at least {{ bannedPasswordMinLength }} characters.</mat-hint>
                        <mat-error *ngIf="isBannedPasswordListMissing">
                            Custom banned password list is required when enforce custom list is enabled.
                        </mat-error>
                    </mat-form-field>
                    <div class="chip-input-error" *ngIf="bannedPasswordInputError">
                        {{ bannedPasswordInputError }}
                    </div>
                </td>
                <td>
                    <sway-baseline-status [hasError]="bannedPasswordListDeviated"></sway-baseline-status>
                </td>
            </tr>



            <!-- Enable password protection on Windows Server Active Directory -->
            <tr>
                <td>
                    <div class="item">
                        Enable password protection on Windows Server Active Directory
                        <mat-icon
                            matTooltip="If set to Yes, password protection is turned on for Active Directory domain controllers when the appropriate agent is installed.">info</mat-icon>
                    </div>
                </td>
                <td>{{ enableBannedPasswordCheckOnPremisesCurrentValue }}</td>
                <td class="desired-value">
                    <mat-slide-toggle formControlName="EnableBannedPasswordCheckOnPremises">
                        {{ form.get('EnableBannedPasswordCheckOnPremises')?.value ? 'Enabled' : 'Disabled' }}
                    </mat-slide-toggle>
                </td>
                <td>
                    <sway-baseline-status [hasError]="enableBannedPasswordCheckOnPremisesDeviated"></sway-baseline-status>
                </td>
            </tr>

            <!-- On-premises protection mode (only when on-premises protection is enabled) -->
            <tr *ngIf="isOnPremisesProtectionEnabled">
                <td>
                    <div class="item">
                        On-premises protection mode
                        <mat-icon
                            matTooltip='If set to Enforce, users will be prevented from setting banned passwords and the attempt will be logged. If set to Audit, the attempt will only be logged.'>info</mat-icon>
                    </div>
                </td>
                <td>{{ bannedPasswordCheckOnPremisesModeCurrentValue }}</td>
                <td class="desired-value">
                    <mat-form-field appearance="fill">
                        <mat-label>Mode</mat-label>
                        <mat-select formControlName="BannedPasswordCheckOnPremisesMode">
                            <mat-option *ngFor="let mode of bannedPasswordModes" [value]="mode.value">
                                {{ mode.label }}
                            </mat-option>
                        </mat-select>
                    </mat-form-field>
                </td>
                <td>
                    <sway-baseline-status [hasError]="bannedPasswordCheckOnPremisesModeDeviated"></sway-baseline-status>
                </td>
            </tr>
        </table>

    </form>
</div>
